import React, { useEffect, useState } from "react";
import { TL } from "../i18n";
import client, { apiErr } from "../lib/api";
import { Button, Card, Select, Badge, Modal } from "./ui";
import { Search, Download, Send, Phone, PhoneOff, HelpCircle, AlertTriangle, History, Megaphone, Cog, Eye, RotateCcw, CalendarClock, Play, Trash2, Power } from "lucide-react";

const DOW = [["0", "Monday"], ["1", "Tuesday"], ["2", "Wednesday"], ["3", "Thursday"], ["4", "Friday"], ["5", "Saturday"], ["6", "Sunday"]];

const STATUS = [
  { v: "all", l: "All" },
  { v: "call", l: "Call list" },
  { v: "dnc", l: "Do-Not-Call" },
  { v: "unknown", l: "Unknown" },
];

export default function ContactsMessaging() {
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ contacts: [], counts: { call: 0, dnc: 0, unknown: 0, total: 0 } });
  const [msg, setMsg] = useState("");
  const [send, setSend] = useState({ call: true, dnc: false, unknown: false, channel: "email", category: "marketing", subject: "Message from Full Option Family Office", message: "" });
  const [busy, setBusy] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campCat, setCampCat] = useState("all");
  const [viewCamp, setViewCamp] = useState(null);
  const [resendMsg, setResendMsg] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [recipFilter, setRecipFilter] = useState("all");
  const [recipQ, setRecipQ] = useState("");
  const [when, setWhen] = useState("now");
  const [sched, setSched] = useState({ schedule_type: "once", run_at: "", time_hhmm: "09:00", day_of_week: 1, day_of_month: 1 });
  const [schedules, setSchedules] = useState([]);

  const loadSchedules = async () => {
    try { const { data } = await client.get("/marketing/schedules"); setSchedules(data); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => { loadSchedules(); }, []);

  const loadCampaigns = async () => {
    try {
      const { data } = await client.get("/marketing/campaigns", { params: { category: campCat === "all" ? undefined : campCat } });
      setCampaigns(data);
      if (viewCamp) setViewCamp(data.find((c) => c.id === viewCamp.id) || null);
      return data;
    } catch (e) { return []; }
  };
  useEffect(() => { loadCampaigns(); /* eslint-disable-next-line */ }, [campCat]);

  // Deep-link: /app/enabler?campaign=<id> opens that campaign's View/Re-send modal.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cid = params.get("campaign");
    if (!cid) return;
    (async () => {
      const data = await loadCampaigns();
      const found = data.find((c) => c.id === cid);
      if (found) { setViewCamp(found); setResendMsg(""); setRecipFilter("all"); setRecipQ(""); }
      window.history.replaceState({}, "", window.location.pathname);
    })();
    /* eslint-disable-next-line */
  }, []);

  const resend = async (mode) => {
    setResendBusy(true); setResendMsg("Re-sending…");
    try {
      const { data } = await client.post(`/marketing/campaigns/${viewCamp.id}/resend`, { mode });
      setResendMsg(data.message || `Re-sent ${data.resent}: ${data.email_sent} email, ${data.sms_sent} SMS (${data.delivered_live} live / ${data.fallback} fallback).`);
      await loadCampaigns();
    } catch (e) { setResendMsg(apiErr(e)); }
    setResendBusy(false);
  };

  const load = async () => {
    try {
      const { data } = await client.get("/marketing/contacts", { params: { status, q: q || undefined } });
      setData(data);
    } catch (e) { setMsg(apiErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const downloadCsv = async () => {
    try {
      const res = await client.get("/marketing/contacts.csv", { params: { status, q: q || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `contacts_${status}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setMsg(apiErr(e)); }
  };

  const targets = ["call", "dnc", "unknown"].filter((t) => send[t]);
  const marketingToDnc = send.category === "marketing" && send.dnc;

  const doSend = async () => {
    if (!send.message.trim()) { setMsg("Enter a message first."); return; }
    if (targets.length === 0) { setMsg("Select at least one target list."); return; }
    setBusy(true);
    try {
      if (when === "schedule") {
        if (sched.schedule_type === "once" && !sched.run_at) { setMsg("Pick a date & time to schedule."); setBusy(false); return; }
        setMsg("Scheduling…");
        await client.post("/marketing/schedules", {
          targets, channel: send.channel, category: send.category, subject: send.subject, message: send.message,
          schedule_type: sched.schedule_type,
          run_at: sched.schedule_type === "once" ? new Date(sched.run_at).toISOString() : null,
          time_hhmm: sched.time_hhmm, day_of_week: Number(sched.day_of_week), day_of_month: Number(sched.day_of_month),
        });
        setMsg("Campaign scheduled.");
        loadSchedules();
      } else {
        setMsg("Sending…");
        const { data } = await client.post("/marketing/bulk-send", {
          targets, channel: send.channel, category: send.category, subject: send.subject, message: send.message,
        });
        setMsg(`Sent to ${data.recipients} recipient(s): ${data.email_sent} email, ${data.sms_sent} SMS (${data.delivered_live} live / ${data.fallback} fallback).`);
        loadCampaigns();
      }
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  const toggleSchedule = async (s) => {
    await client.post(`/marketing/schedules/${s.id}/toggle`, { enabled: !s.enabled }); loadSchedules();
  };
  const runScheduleNow = async (s) => {
    setMsg("Running scheduled campaign now…");
    try { const { data } = await client.post(`/marketing/schedules/${s.id}/run-now`); setMsg(`Ran now: ${data.recipients} recipient(s).`); loadCampaigns(); loadSchedules(); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const deleteSchedule = async (s) => {
    if (!window.confirm("Delete this scheduled campaign?")) return;
    await client.delete(`/marketing/schedules/${s.id}`); loadSchedules();
  };

  const filteredRecipients = (viewCamp?.delivery_log || []).filter((e) => {
    const delivered = e.status === "sent";
    if (recipFilter === "delivered" && !delivered) return false;
    if (recipFilter === "undelivered" && delivered) return false;
    if (recipQ) {
      const q = recipQ.toLowerCase();
      return (e.to || "").toLowerCase().includes(q) || (e.name || "").toLowerCase().includes(q);
    }
    return true;
  });

  const exportRecipientsCsv = () => {
    const rows = [["Recipient", "Name", "Channel", "Status"],
      ...filteredRecipients.map((e) => [e.to || "", e.name || "", e.channel, e.status === "sent" ? "delivered" : "undelivered"])];
    const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `campaign_recipients_${recipFilter}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const catBadge = (c) => c === "call"
    ? <Badge color="success"><Phone size={11} className="inline" /> {TL("Call")}</Badge>
    : c === "dnc" ? <Badge color="danger"><PhoneOff size={11} className="inline" /> {TL("Do-Not-Call")}</Badge>
      : <Badge color="warning"><HelpCircle size={11} className="inline" /> {TL("Unknown")}</Badge>;

  return (
    <Card className="space-y-4" data-testid="contacts-messaging">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-head text-lg">{TL("Contacts — Call / Do-Not-Call lists")}</h2>
        <div className="ms-auto flex gap-2 text-xs">
          <Badge color="success">Call {data.counts.call}</Badge>
          <Badge color="danger">DNC {data.counts.dnc}</Badge>
          <Badge color="warning">Unknown {data.counts.unknown}</Badge>
          <Badge color="info">Total {data.counts.total}</Badge>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="w-44"><Select label={TL("List")} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="cm-status">
          {STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </Select></div>
        <div className="flex flex-1 items-center gap-2 rounded-md border border-black/15 dark:border-white/15 px-3 py-2">
          <Search size={15} className="opacity-50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder={TL("Search email / phone / name…")} className="w-full bg-transparent text-sm outline-none" data-testid="cm-search" />
        </div>
        <Button variant="ghost" onClick={load} data-testid="cm-search-btn">{TL("Search")}</Button>
        <Button variant="ghost" onClick={downloadCsv} data-testid="cm-csv"><Download size={15} /> {TL("CSV")}</Button>
      </div>

      <div className="max-h-60 overflow-y-auto rounded-md border border-black/10 dark:border-white/10">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-black/[0.04] dark:bg-white/[0.06]"><tr>
            <th className="p-2 text-start font-medium opacity-70">{TL("Email")}</th>
            <th className="p-2 text-start font-medium opacity-70">{TL("Phone")}</th>
            <th className="p-2 text-start font-medium opacity-70">{TL("Source")}</th>
            <th className="p-2 text-start font-medium opacity-70">{TL("List")}</th>
          </tr></thead>
          <tbody data-testid="cm-rows">
            {data.contacts.length === 0 && <tr><td colSpan={4} className="p-3 text-center opacity-50">{TL("No contacts.")}</td></tr>}
            {data.contacts.map((c) => (
              <tr key={c.email} className="border-t border-black/5 dark:border-white/5">
                <td className="p-2">{c.email}</td>
                <td className="p-2">{c.phone || "—"}</td>
                <td className="p-2 capitalize opacity-70">{c.source}</td>
                <td className="p-2">{catBadge(c.category)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 rounded-lg border border-black/10 dark:border-white/10 p-3">
        <div className="font-head text-sm flex items-center gap-2"><Send size={15} className="text-[var(--primary)]" /> {TL("Bulk message")}</div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="opacity-70">{TL("Send to:")}</span>
          {["call", "dnc", "unknown"].map((t) => (
            <label key={t} className="flex items-center gap-1.5">
              <input type="checkbox" checked={send[t]} onChange={(e) => setSend({ ...send, [t]: e.target.checked })} data-testid={`cm-target-${t}`} />
              {t === "call" ? "Call list" : t === "dnc" ? "Do-Not-Call" : "Unknown"}
            </label>
          ))}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Select label={TL("Channel")} value={send.channel} onChange={(e) => setSend({ ...send, channel: e.target.value })} data-testid="cm-channel">
            <option value="email">{TL("Email")}</option><option value="sms">{TL("SMS")}</option><option value="both">{TL("Email + SMS")}</option>
          </Select>
          <Select label={TL("Message type")} value={send.category} onChange={(e) => setSend({ ...send, category: e.target.value })} data-testid="cm-category">
            <option value="marketing">{TL("Advertising / Marketing")}</option><option value="system">{TL("System-generated")}</option><option value="program">{TL("Program-related")}</option>
          </Select>
        </div>
        {(send.channel === "email" || send.channel === "both") && (
          <input value={send.subject} onChange={(e) => setSend({ ...send, subject: e.target.value })} placeholder={TL("Email subject")} data-testid="cm-subject"
            className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
        )}
        <textarea rows={4} value={send.message} onChange={(e) => setSend({ ...send, message: e.target.value })} placeholder={TL("Message body")} data-testid="cm-message"
          className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
        {marketingToDnc && (
          <div className="flex items-center gap-2 rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300" data-testid="cm-dnc-warning">
            <AlertTriangle size={14} /> {TL("You are about to send a")} <b>{TL("marketing")}</b> {TL("message to the")} <b>{TL("Do-Not-Call")}</b> list. Ensure you have a lawful basis to do so.
          </div>
        )}

        <div className="space-y-2 rounded-md border border-black/10 dark:border-white/10 p-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-40"><Select label={TL("When")} value={when} onChange={(e) => setWhen(e.target.value)} data-testid="cm-when">
              <option value="now">{TL("Send now")}</option><option value="schedule">{TL("Schedule")}</option>
            </Select></div>
            {when === "schedule" && (
              <div className="w-44"><Select label={TL("Frequency")} value={sched.schedule_type} onChange={(e) => setSched({ ...sched, schedule_type: e.target.value })} data-testid="cm-sched-type">
                <option value="once">{TL("Once")}</option><option value="daily">{TL("Daily")}</option><option value="weekly">{TL("Weekly")}</option><option value="monthly">{TL("Monthly")}</option>
              </Select></div>
            )}
            {when === "schedule" && sched.schedule_type === "once" && (
              <label className="block"><span className="mb-1 block text-xs font-medium opacity-70">{TL("Date & time")}</span>
                <input type="datetime-local" value={sched.run_at} onChange={(e) => setSched({ ...sched, run_at: e.target.value })} data-testid="cm-sched-runat"
                  className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" /></label>
            )}
            {when === "schedule" && sched.schedule_type !== "once" && (
              <label className="block"><span className="mb-1 block text-xs font-medium opacity-70">{TL("Time")}</span>
                <input type="time" value={sched.time_hhmm} onChange={(e) => setSched({ ...sched, time_hhmm: e.target.value })} data-testid="cm-sched-time"
                  className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" /></label>
            )}
            {when === "schedule" && sched.schedule_type === "weekly" && (
              <div className="w-36"><Select label={TL("Day")} value={sched.day_of_week} onChange={(e) => setSched({ ...sched, day_of_week: e.target.value })} data-testid="cm-sched-dow">
                {DOW.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select></div>
            )}
            {when === "schedule" && sched.schedule_type === "monthly" && (
              <div className="w-28"><Select label={TL("Day of month")} value={sched.day_of_month} onChange={(e) => setSched({ ...sched, day_of_month: e.target.value })} data-testid="cm-sched-dom">
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </Select></div>
            )}
          </div>
          <div className="flex justify-end">
            <Button onClick={doSend} disabled={busy} data-testid="cm-send">
              {when === "schedule" ? <><CalendarClock size={15} /> {TL("Schedule campaign")}</> : <><Send size={15} /> {TL("Send bulk message")}</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-black/10 dark:border-white/10 p-3" data-testid="scheduled-campaigns">
        <div className="font-head text-sm flex items-center gap-2"><CalendarClock size={15} className="text-[var(--primary)]" /> {TL("Scheduled campaigns")}</div>
        <div className="space-y-2" data-testid="sc-rows">
          {schedules.length === 0 && <div className="text-sm opacity-50">{TL("No scheduled campaigns.")}</div>}
          {schedules.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-2 rounded-md border border-black/10 dark:border-white/10 px-3 py-2 text-sm">
              {s.category === "marketing" ? <Badge color="info"><Megaphone size={11} className="inline" /> {TL("Advertising")}</Badge> : <Badge color="neutral">{s.category === "system" ? "System" : "Program"}</Badge>}
              <span className="capitalize">{s.schedule_type}</span>
              <span className="opacity-60">{s.channel === "both" ? "Email+SMS" : s.channel} · {(s.targets || []).join(", ")}</span>
              <span className="opacity-60">{s.next_run ? `next ${s.next_run.slice(0, 16).replace("T", " ")}` : "—"}</span>
              {s.enabled ? <Badge color="success">{TL("active")}</Badge> : <Badge color="warning">{TL("paused")}</Badge>}
              <div className="ms-auto flex gap-1">
                <button onClick={() => runScheduleNow(s)} title={TL("Run now")} data-testid={`sc-run-${s.id}`} className="rounded-md border border-black/15 dark:border-white/15 p-1.5 lift"><Play size={13} /></button>
                <button onClick={() => toggleSchedule(s)} title={s.enabled ? "Pause" : "Resume"} data-testid={`sc-toggle-${s.id}`} className="rounded-md border border-black/15 dark:border-white/15 p-1.5 lift"><Power size={13} /></button>
                <button onClick={() => deleteSchedule(s)} title={TL("Delete")} data-testid={`sc-del-${s.id}`} className="rounded-md border border-black/15 dark:border-white/15 p-1.5 lift text-[#DA1E28]"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {msg && <div className="rounded-md border border-[var(--primary)]/40 px-3 py-2 text-sm" data-testid="cm-msg">{msg}</div>}

      <div className="space-y-3 rounded-lg border border-black/10 dark:border-white/10 p-3" data-testid="campaign-history">
        <div className="flex flex-wrap items-center gap-3">
          <div className="font-head text-sm flex items-center gap-2"><History size={15} className="text-[var(--primary)]" /> {TL("Campaign history")}</div>
          <div className="ms-auto w-52">
            <Select value={campCat} onChange={(e) => setCampCat(e.target.value)} data-testid="ch-filter">
              <option value="all">{TL("All message types")}</option>
              <option value="marketing">{TL("Advertising / Marketing")}</option>
              <option value="system">{TL("System-generated")}</option>
              <option value="program">{TL("Program-related")}</option>
            </Select>
          </div>
        </div>
        <div className="max-h-60 overflow-y-auto rounded-md border border-black/10 dark:border-white/10">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-black/[0.04] dark:bg-white/[0.06]"><tr>
              <th className="p-2 text-start font-medium opacity-70">{TL("Date")}</th>
              <th className="p-2 text-start font-medium opacity-70">{TL("Type")}</th>
              <th className="p-2 text-start font-medium opacity-70">{TL("Channel")}</th>
              <th className="p-2 text-start font-medium opacity-70">{TL("Lists")}</th>
              <th className="p-2 text-end font-medium opacity-70">{TL("Recipients")}</th>
              <th className="p-2 text-end font-medium opacity-70">{TL("Email")}</th>
              <th className="p-2 text-end font-medium opacity-70">{TL("SMS")}</th>
              <th className="p-2 text-end font-medium opacity-70"></th>
            </tr></thead>
            <tbody data-testid="ch-rows">
              {campaigns.length === 0 && <tr><td colSpan={8} className="p-3 text-center opacity-50">{TL("No campaigns sent yet.")}</td></tr>}
              {campaigns.map((c, i) => (
                <tr key={c.id || i} className="border-t border-black/5 dark:border-white/5" title={c.message}>
                  <td className="p-2 whitespace-nowrap">{(c.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-2">{c.category === "marketing"
                    ? <Badge color="info"><Megaphone size={11} className="inline" /> {TL("Advertising")}</Badge>
                    : <Badge color="neutral"><Cog size={11} className="inline" /> {c.category === "system" ? "System" : "Program"}</Badge>}</td>
                  <td className="p-2 capitalize">{c.channel === "both" ? "Email+SMS" : c.channel}</td>
                  <td className="p-2 text-xs opacity-70">{(c.targets || []).map((t) => t === "dnc" ? "Do-Not-Call" : t === "call" ? "Call" : "Unknown").join(", ")}</td>
                  <td className="p-2 text-end">{c.recipients}</td>
                  <td className="p-2 text-end">{c.email_sent}</td>
                  <td className="p-2 text-end">{c.sms_sent}</td>
                  <td className="p-2 text-end">
                    <button onClick={() => { setViewCamp(c); setResendMsg(""); setRecipFilter("all"); setRecipQ(""); }} data-testid={`ch-view-${i}`}
                      className="inline-flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift">
                      <Eye size={12} /> View{c.undelivered ? ` (${c.undelivered}!)` : ""}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={!!viewCamp} onClose={() => setViewCamp(null)} title={TL("Campaign details")} testid="campaign-detail-modal">
        {viewCamp && (
          <div className="space-y-3 text-sm">
            <div className="flex flex-wrap gap-2">
              {viewCamp.category === "marketing"
                ? <Badge color="info"><Megaphone size={11} className="inline" /> {TL("Advertising")}</Badge>
                : <Badge color="neutral">{viewCamp.category === "system" ? "System" : "Program"}</Badge>}
              <Badge color="info">{viewCamp.channel === "both" ? "Email+SMS" : viewCamp.channel}</Badge>
              <Badge color="success">{viewCamp.recipients} recipients</Badge>
              {viewCamp.undelivered > 0 ? <Badge color="warning">{viewCamp.undelivered} undelivered</Badge> : <Badge color="success">{TL("all delivered")}</Badge>}
            </div>
            <div className="opacity-70 text-xs">{(viewCamp.created_at || "").slice(0, 16).replace("T", " ")} · by {viewCamp.sent_by} · lists: {(viewCamp.targets || []).join(", ")}</div>
            {viewCamp.subject && <div><span className="opacity-60">{TL("Subject:")} </span><b>{viewCamp.subject}</b></div>}
            <div className="rounded-md border border-black/10 dark:border-white/10 bg-black/[0.03] dark:bg-white/[0.04] p-3 whitespace-pre-wrap" data-testid="cd-message">{viewCamp.message}</div>

            <div data-testid="cd-recipients">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <div className="text-xs font-medium uppercase tracking-wide opacity-60">Per-recipient delivery ({(viewCamp.delivery_log || []).length})</div>
                <div className="ms-auto flex items-center gap-2">
                  <div className="w-36"><Select value={recipFilter} onChange={(e) => setRecipFilter(e.target.value)} data-testid="cd-recip-filter">
                    <option value="all">{TL("All")}</option><option value="delivered">{TL("Delivered")}</option><option value="undelivered">{TL("Undelivered")}</option>
                  </Select></div>
                  <div className="flex items-center gap-1.5 rounded-md border border-black/15 dark:border-white/15 px-2 py-1.5">
                    <Search size={13} className="opacity-50" />
                    <input value={recipQ} onChange={(e) => setRecipQ(e.target.value)} placeholder={TL("Find recipient…")} data-testid="cd-recip-search"
                      className="w-36 bg-transparent text-xs outline-none" />
                  </div>
                  <button onClick={exportRecipientsCsv} disabled={filteredRecipients.length === 0} data-testid="cd-recip-csv"
                    className="inline-flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1.5 text-xs lift disabled:opacity-40">
                    <Download size={13} /> CSV
                  </button>
                </div>
              </div>
              {(() => {
                const filtered = filteredRecipients;
                return (
                  <div className="max-h-44 overflow-y-auto rounded-md border border-black/10 dark:border-white/10">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-black/[0.04] dark:bg-white/[0.06]"><tr>
                        <th className="p-1.5 text-start font-medium opacity-70">{TL("Recipient")}</th>
                        <th className="p-1.5 text-start font-medium opacity-70">{TL("Channel")}</th>
                        <th className="p-1.5 text-end font-medium opacity-70">{TL("Status")}</th>
                      </tr></thead>
                      <tbody data-testid="cd-recip-rows">
                        {filtered.length === 0 && <tr><td colSpan={3} className="p-2 text-center opacity-50">{TL("No matching recipients.")}</td></tr>}
                        {filtered.map((e, j) => (
                          <tr key={j} className="border-t border-black/5 dark:border-white/5">
                            <td className="p-1.5">{e.to}{e.name ? <span className="opacity-50"> · {e.name}</span> : ""}</td>
                            <td className="p-1.5 capitalize">{e.channel}</td>
                            <td className="p-1.5 text-end">{e.status === "sent"
                              ? <Badge color="success">{TL("delivered")}</Badge>
                              : <Badge color="warning">{TL("undelivered")}</Badge>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>

            {resendMsg && <div className="rounded-md border border-[var(--primary)]/40 px-3 py-2" data-testid="cd-resend-msg">{resendMsg}</div>}
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={() => setViewCamp(null)}>{TL("Close")}</Button>
              <Button variant="subtle" onClick={() => resend("all")} disabled={resendBusy} data-testid="cd-resend-all"><RotateCcw size={14} /> Re-send to all ({viewCamp.recipients})</Button>
              <Button onClick={() => resend("undelivered")} disabled={resendBusy || !viewCamp.undelivered} data-testid="cd-resend-undelivered"><Send size={14} /> Re-send undelivered ({viewCamp.undelivered || 0})</Button>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
}
